import React, { useState } from 'react';
import { X, Save, AlertTriangle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const AdjustmentModal = ({ isOpen, onClose, itemData, onComplete }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [newQty, setNewQty] = useState(itemData?.curbal || 0);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await axios.post('/api/inventory/adjust', {
        itemnum: itemData.itemnum,
        location: itemData.location,
        new_qty: newQty,
        remarks: remarks
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.data.status === 'SUCCESS') {
        toast.success(t('adjustmentModal.adjustedToast', { adjustment: res.data.adjustment }));
        onComplete(); // تحديث الجدول في الشاشة الرئيسية
        onClose();
      } else {
        toast(t('adjustmentModal.noChangesToast'), { icon: 'ℹ️' });
      }
    } catch (err) {
      toast.error(t('adjustmentModal.failToast', { detail: err.response?.data?.detail || t('adjustmentModal.systemError') }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 ${isRTL ? 'font-cairo' : 'font-sans'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-surface-card w-full max-w-md rounded-sm shadow-2xl border-t-4 border-t-accent animate-in zoom-in-95 duration-200 uppercase">

        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-surface-panel">
          <h3 className="font-black text-ink text-[10px] tracking-widest flex items-center gap-2">
            <RefreshCw size={14} className="text-accent" /> {t('adjustmentModal.title')}
          </h3>
          <button onClick={onClose} className="text-ink-secondary hover:text-status-danger"><X size={18}/></button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          <div className="flex justify-between items-center bg-primary-soft p-4 border border-primary/20 rounded-sm">
             <div>
                <p className="text-[8px] font-black text-primary/70">{t('adjustmentModal.currentBalance')}</p>
                <p className="text-xl font-black text-primary">{itemData?.curbal} <small className="text-[10px]">{itemData?.orderunit}</small></p>
             </div>
             <div className={isRTL ? 'text-left' : 'text-right'}>
                <p className="text-[8px] font-black text-primary/70">{t('adjustmentModal.variance')}</p>
                <p className={`text-xl font-black ${(newQty - itemData?.curbal) < 0 ? 'text-status-danger' : 'text-status-success'}`}>
                    {(newQty - itemData?.curbal) > 0 ? `+${newQty - itemData?.curbal}` : newQty - itemData?.curbal}
                </p>
             </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col gap-1 border-b focus-within:border-accent transition-all">
                <label className="text-[9px] font-black text-ink-secondary italic">{t('adjustmentModal.actualCount')}</label>
                <input
                    type="number"
                    autoFocus
                    className="outline-none py-2 font-black text-lg text-ink"
                    value={newQty}
                    onChange={(e) => setNewQty(e.target.value)}
                />
            </div>

            <div className="flex flex-col gap-1 border-b focus-within:border-accent transition-all">
                <label className="text-[9px] font-black text-ink-secondary italic">{t('adjustmentModal.reasonLabel')}</label>
                <textarea
                    className="outline-none py-2 font-bold text-[10px] h-16 resize-none"
                    placeholder={t('adjustmentModal.reasonPlaceholder')}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                />
            </div>
          </div>

          {Math.abs(newQty - itemData?.curbal) > (itemData?.curbal * 0.2) && (
              <div className="bg-status-warning-bg border border-status-warning/30 p-3 rounded-sm flex items-start gap-3">
                  <AlertTriangle className="text-status-warning shrink-0" size={16}/>
                  <p className="text-[9px] font-bold text-status-warning lowercase leading-tight normal-case">
                    {t('adjustmentModal.warningText')}
                  </p>
              </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-surface-panel border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2 text-[10px] font-black text-ink-secondary hover:text-ink uppercase tracking-widest">{t('common.cancel', 'Cancel')}</button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-accent hover:bg-accent-hover text-ink-inverse px-8 py-2 rounded-sm font-black text-[10px] uppercase flex items-center gap-2 shadow-lg transition-all active:scale-95 disabled:opacity-50"
          >
            <Save size={14}/> {loading ? t('adjustmentModal.saving') : t('adjustmentModal.postAdjustment')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdjustmentModal;
